import type { DomWallpaperContent } from '../wallpaperRenderer'
import { CUBEMAP, CUBE_FACES, FPS_CAP, HASHNOISE, IMAGE, MAX_BUFFER_SIZE, NOISE2D, NOISE_CUBE, PASS_KINDS } from './types'
import type { FilterName, PassRuntime, PassSpec, UniformLocs } from './types'
import { canonicalKey, parseShaderSource } from './parse'
import { buildProgram, passSrc } from './glsl'
import { createNoiseTexture, pickFilter, pickFmt } from './textures'

/**
 * Shadertoy 着色器壁纸 — 独立 canvas + WebGL2 多 pass 管线。
 *
 * 格式: 每个 pass 用带标签代码块, 未包裹裸代码 = image pass。
 * 可用标签: common / bufferA..D / cubemapA / image / sound(忽略)。
 *
 * 通道接线(pass 内注释): `// iChannelN: <目标>`, 目标可为 pass 名 / `cube`(噪点 cubemap) / `hashnoise`(白噪点);
 * 未指定时: 多 buffer(≥2) 按 iChannelN→第N个buffer 自动接线; 单 buffer 时 image.iChannel0 自动接该 buffer;
 * 其余通道采样平滑噪点 2D 纹理。
 * 采样选项(可选, 追加在目标后): `// iChannel0: bufferA mipmap repeat` — filter: none/linear/mipmap, wrap: clamp/repeat。
 * 其他可选注释: `// iResolution: WxH` 指定该 pass 离屏分辨率。
 *
 * 渲染顺序: bufferA→bufferD → cubemapA → image。
 * buffer 用 RGBA32F 跟随画布(钳制 MAX_BUFFER_SIZE), cubemap 固定 1024×1024 RGBA16F。
 * cubemap pass 内可用 uniform `iCubeFace`(0..5) 计算方向。
 */
export class ShaderContent implements DomWallpaperContent {
  private canvas: HTMLCanvasElement | null = null
  private gl: WebGL2RenderingContext | null = null
  private source = ''            // 当前着色器源码(用户代码)
  private programKey = ''        // 已编译管线的缓存键
  private animId = 0
  private startTime = 0
  private prevTime = 0
  private lastRenderTime = 0
  private lastDateSec = -1
  private cachedDate = new Float32Array(4)
  private chanTime = new Float32Array(4) // 复用的 iChannelTime 值, 避免每帧分配
  private mouse4 = new Float32Array(4)   // 复用的 iMouse 值
  private passes: PassRuntime[] = []
  /** kind → PassRuntime 索引, 避免每帧 resolveChannel 做 Array.find */
  private passByKind = new Map<string, PassRuntime>()
  private vbuf: WebGLBuffer | null = null
  private fboFmt = { internal: 0, type: 0 }
  private bufferFilter = 0  // buffer 纹理 min/mag filter(NEAREST 或 LINEAR)
  private cubeFmt = { internal: 0, type: 0 }
  private cubeFilter = 0    // cubemap 纹理 min/mag filter(RGBA16F + LINEAR)
  private bufferLinearOk = false // OES_texture_float_linear(channel 显式 mipmap/linear 的能力判断)
  private cubeLinearOk = false   // OES_texture_half_float_linear
  private mipmaped = new Set<WebGLTexture>() // 已生成 mipmap 链的纹理(惰性去重)
  private mouse = { x: 0, y: 0, cx: 0, cy: 0, down: false, signal: false }
  private onMouse = (e: MouseEvent) => {
    // destroy 移除监听与事件已在队列之间的时序竞争, 避免 canvas 为 null
    const c = this.canvas
    if (!c) return
    const r = c.getBoundingClientRect()
    // iMouse 坐标系: WebGL 左下原点(y 向上), DOM 左上原点需翻转 y
    const mx = e.clientX - r.left
    const my = r.height - (e.clientY - r.top)
    // xy 仅按下时更新; z/w 存按下位置(未按下取负)
    if (e.type === 'mousedown') { this.mouse.cx = mx; this.mouse.cy = my; this.mouse.down = true; this.mouse.signal = true }
    if (e.type === 'mouseup' || e.type === 'mouseleave') this.mouse.down = false
    if (this.mouse.down) { this.mouse.x = mx; this.mouse.y = my }
  }

  constructor(source: string) {
    this.source = source
  }

  async renderTo(wrapper: HTMLElement): Promise<void> {
    const canvas = document.createElement('canvas')
    const dpr = window.devicePixelRatio || 1
    const w = wrapper.clientWidth
    const h = wrapper.clientHeight
    canvas.width = Math.floor(w * dpr)
    canvas.height = Math.floor(h * dpr)
    canvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%'

    const gl = canvas.getContext('webgl2') as WebGL2RenderingContext | null
    if (!gl) throw new Error('WebGL 2.0 不可用')

    this.setup(gl, canvas)

    wrapper.appendChild(canvas)
    canvas.addEventListener('mousemove', this.onMouse)
    canvas.addEventListener('mousedown', this.onMouse)
    canvas.addEventListener('mouseup', this.onMouse)
    canvas.addEventListener('mouseleave', this.onMouse)
    canvas.addEventListener('webglcontextlost', this.onContextLost)
    canvas.addEventListener('webglcontextrestored', this.onContextRestored)
    this.tick()
  }

  /** 初始化(首次 / context 恢复): 顶点缓冲 + 噪点 + 全管线编译 */
  private setup(gl: WebGL2RenderingContext, c: HTMLCanvasElement): void {
    this.canvas = c
    this.gl = gl
    const buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.bindBuffer(gl.ARRAY_BUFFER, null)
    this.vbuf = buf
    // buffer 用 RGBA32F 全精度: RGBA16F 在重投影类 shader 高速运动时量化误差导致抖动; cubemap 用 RGBA16F
    this.fboFmt = pickFmt(gl, 'float32')
    this.bufferFilter = pickFilter(gl, this.fboFmt.type)
    this.cubeFmt = pickFmt(gl, 'float16')
    this.cubeFilter = pickFilter(gl, this.cubeFmt.type)
    // 缓存 float 纹理 linear 采样扩展(供 channel 显式 mipmap/linear 的能力判断)
    this.bufferLinearOk = !!gl.getExtension('OES_texture_float_linear')
    this.cubeLinearOk = !!gl.getExtension('OES_texture_half_float_linear')

    const parsed = parseShaderSource(this.source)
    this.setPasses(this.buildPipeline(parsed.common, parsed.passes))
    this.programKey = canonicalKey(parsed.common, parsed.passes)
    this.startTime = performance.now()
  }

  /** 更新着色器源码(复用同一 canvas); 解析/编译失败时保留上一版本管线 */
  updateShader(source: string): void {
    if (source === this.source) return
    const gl = this.gl, c = this.canvas
    if (!gl || !c) return

    const prevSource = this.source
    try {
      this.source = source
      const parsed = parseShaderSource(source)
      const key = canonicalKey(parsed.common, parsed.passes)
      if (key === this.programKey) return
      const rts = this.buildPipeline(parsed.common, parsed.passes)
      this.disposeRuntime()
      this.setPasses(rts)
      this.programKey = key
    } catch (e) {
      this.source = prevSource // 失败统一回滚, 保持与当前画面一致
      console.warn('着色器更新失败, 保留上一版本:', (e as Error).message)
    }
  }

  /* ── 管线构建 ── */

  /** 统一赋值 passes 并同步维护 kind 索引(所有 passes 变更入口) */
  private setPasses(rts: PassRuntime[]): void {
    this.passes = rts
    this.passByKind = new Map(rts.map(r => [r.spec.kind, r]))
  }

  /** 编译全部 pass 程序并分配离屏目标; 任一失败则清理并抛出 */
  private buildPipeline(common: string, specs: PassSpec[]): PassRuntime[] {
    const gl = this.gl!
    const rts: PassRuntime[] = []
    const byKind = new Map(specs.map(s => [s.kind, s]))
    try {
      for (const kind of PASS_KINDS) {
        const spec = byKind.get(kind)
        if (!spec) continue
        const chTypes = spec.channels.map(c =>
          (c.target === NOISE_CUBE || c.target === CUBEMAP) ? 'samplerCube' : 'sampler2D')
        const { prog, err } = buildProgram(gl, passSrc(common, spec.code, chTypes))
        if (!prog) throw new Error(`${kind} pass ${err}`)
        // 本 pass 输出是否被下游以 mipmap 采样(渲染后需生成 mipmap 链)
        const mipmapNeeded = specs.some(s => s !== spec &&
          s.channels.some(ch => ch.target === kind && ch.filter === 'mipmap'))
        const rt: PassRuntime = {
          spec, prog,
          // 临时空对象, bindProgramState 紧接着完整填充所有字段
          uLoc: {} as UniformLocs, cTimeLoc: null, resLoc: null, faceLoc: null,
          fbo: null, textures: [], noiseTex: [null, null, null, null],
          readIdx: 0, frame: 0, mipmapNeeded, w: 0, h: 0, chanRes: new Float32Array(12),
        }
        this.bindProgramState(rt)
        this.allocTarget(rt)
        rts.push(rt)
        this.refreshChannelRes(rt, rts)
      }
      return rts
    } catch (e) {
      for (const rt of rts) this.disposePass(rt)
      throw e
    }
  }

  private bindProgramState(rt: PassRuntime): void {
    const gl = this.gl!
    gl.useProgram(rt.prog)
    for (let i = 0; i < 4; i++) {
      const loc = gl.getUniformLocation(rt.prog, 'iChannel' + i)
      if (loc) gl.uniform1i(loc, i)
    }
    rt.uLoc = {
      iTime: gl.getUniformLocation(rt.prog, 'iTime'),
      iResolution: gl.getUniformLocation(rt.prog, 'iResolution'),
      iMouse: gl.getUniformLocation(rt.prog, 'iMouse'),
      iDate: gl.getUniformLocation(rt.prog, 'iDate'),
      iFrame: gl.getUniformLocation(rt.prog, 'iFrame'),
      iTimeDelta: gl.getUniformLocation(rt.prog, 'iTimeDelta'),
      iFrameRate: gl.getUniformLocation(rt.prog, 'iFrameRate'),
      iSampleRate: gl.getUniformLocation(rt.prog, 'iSampleRate'),
    }
    if (rt.uLoc.iSampleRate) gl.uniform1f(rt.uLoc.iSampleRate, 44100)
    rt.cTimeLoc = gl.getUniformLocation(rt.prog, 'iChannelTime')
    rt.resLoc = gl.getUniformLocation(rt.prog, 'iChannelResolution')
    rt.faceLoc = gl.getUniformLocation(rt.prog, 'iCubeFace')
  }

  /** 分配该 pass 的离屏渲染目标(buffer: 双纹理 ping-pong; cubemap: 6 面) */
  private allocTarget(rt: PassRuntime): void {
    const gl = this.gl!
    const c = this.canvas!
    if (rt.spec.kind === IMAGE) return
    let w = rt.spec.w || c.width
    let h = rt.spec.h || c.height
    // 保持宽高比钳制到上限: 全屏壁纸(4K + DPR)下 RGBA32F 离屏目标会耗尽显存, 触发驱动崩溃/黑屏
    if (w > MAX_BUFFER_SIZE || h > MAX_BUFFER_SIZE) {
      const s = Math.min(MAX_BUFFER_SIZE / w, MAX_BUFFER_SIZE / h)
      w = Math.max(1, Math.floor(w * s))
      h = Math.max(1, Math.floor(h * s))
    }
    rt.w = w; rt.h = h

    if (rt.spec.kind === CUBEMAP) {
      // cubemap 固定 1024x1024(与画布无关); 格式 RGBA16F + LINEAR
      rt.w = 1024; rt.h = 1024
      const fmt = this.cubeFmt
      const tex = gl.createTexture()!
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, tex)
      for (const f of CUBE_FACES) {
        gl.texImage2D(f, 0, fmt.internal, rt.w, rt.h, 0, gl.RGBA, fmt.type, null)
      }
      gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, this.cubeFilter)
      gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, this.cubeFilter)
      gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE)
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, null)
      const fbo = gl.createFramebuffer()!
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
      gl.clearColor(0, 0, 0, 0)
      for (const f of CUBE_FACES) {
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, f, tex, 0)
        gl.clear(gl.COLOR_BUFFER_BIT)
      }
      rt.textures = [tex]
      rt.fbo = fbo
    } else {
      // buffer: 仅当自反馈(采样自身上一帧)时才需要双纹理 ping-pong
      const fmt = this.fboFmt
      const needPingPong = rt.spec.channels.some(ch => ch.target === rt.spec.kind)
      const count = needPingPong ? 2 : 1
      for (let k = 0; k < count; k++) {
        const tex = gl.createTexture()!
        gl.bindTexture(gl.TEXTURE_2D, tex)
        gl.texImage2D(gl.TEXTURE_2D, 0, fmt.internal, w, h, 0, gl.RGBA, fmt.type, null)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, this.bufferFilter)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, this.bufferFilter)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        rt.textures.push(tex)
      }
      gl.bindTexture(gl.TEXTURE_2D, null)
      const fbo = gl.createFramebuffer()!
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
      gl.clearColor(0, 0, 0, 0)
      for (let k = 0; k < count; k++) {
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, rt.textures[k], 0)
        gl.clear(gl.COLOR_BUFFER_BIT)
      }
      rt.fbo = fbo
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  }

  /** 各通道的 iChannelResolution(跟随被采样目标尺寸) */
  private refreshChannelRes(rt: PassRuntime, list: PassRuntime[]): void {
    const res = rt.chanRes
    for (let i = 0; i < 4; i++) {
      const t = rt.spec.channels[i].target
      let w = 256, h = 256
      const src = t && t !== NOISE2D && t !== NOISE_CUBE && t !== HASHNOISE && t !== IMAGE
        ? list.find(p => p.spec.kind === t) : undefined
      if (src) { w = src.w || 256; h = src.h || 256 }
      res[i * 3] = w; res[i * 3 + 1] = h; res[i * 3 + 2] = 1
    }
  }

  /**
   * 解析某 pass 的第 i 个通道应绑定的纹理(未接线/无效目标 → 平滑噪点)。
   * 返回纹理 + 该 channel 显式指定的采样参数(未指定为 undefined, 用纹理默认)。
   */
  private resolveChannel(rt: PassRuntime, i: number): ResolvedChannel {
    const gl = this.gl!
    const ch = rt.spec.channels[i]
    const t = ch.target
    let tex: WebGLTexture
    let cube = false
    let floatTex = false // pass 输出(float)的 mipmap 需对应扩展; 噪点(8bit)无限制
    if (t === NOISE_CUBE) { tex = this.resolveNoise(rt, i, true, false); cube = true }
    else if (t === HASHNOISE) tex = this.resolveNoise(rt, i, false, true)
    else if (t && t !== NOISE2D && t !== IMAGE) {
      const src = this.passByKind.get(t)
      if (src) {
        tex = t === CUBEMAP ? src.textures[0]
          // 双纹理 ping-pong 读 readIdx(最新帧); 单纹理始终读第 0 张
          : src.textures.length > 1 ? src.textures[src.readIdx] : src.textures[0]
        cube = t === CUBEMAP
        floatTex = true
      } else {
        tex = this.resolveNoise(rt, i, false, false)
      }
    } else {
      tex = this.resolveNoise(rt, i, false, false)
    }
    return {
      tex, cube,
      filter: this.resolveChannelFilter(gl, floatTex, cube, ch.filter),
      wrap: ch.wrap === 'repeat' ? gl.REPEAT : ch.wrap === 'clamp' ? gl.CLAMP_TO_EDGE : undefined,
    }
  }

  /** 获取/懒创建该 pass 第 i 通道的程序化噪点纹理(每通道独立实例, 采样参数互不干扰) */
  private resolveNoise(rt: PassRuntime, i: number, cube: boolean, white: boolean): WebGLTexture {
    let tex = rt.noiseTex[i]
    if (!tex) {
      tex = createNoiseTexture(this.gl!, cube, white)
      rt.noiseTex[i] = tex
    }
    return tex
  }

  /** channel 显式 filter → GL 采样参数; float 纹理 mipmap 需对应 linear 扩展, 不支持时降级 LINEAR */
  private resolveChannelFilter(
    gl: WebGL2RenderingContext, floatTex: boolean, cube: boolean, name?: FilterName,
  ): { min: number; mag: number; mipmap: boolean } | undefined {
    if (!name) return undefined
    if (name === 'none') return { min: gl.NEAREST, mag: gl.NEAREST, mipmap: false }
    if (name === 'linear') return { min: gl.LINEAR, mag: gl.LINEAR, mipmap: false }
    const linearOk = !floatTex || (cube ? this.cubeLinearOk : this.bufferLinearOk)
    return linearOk
      ? { min: gl.LINEAR_MIPMAP_LINEAR, mag: gl.LINEAR, mipmap: true }
      : { min: gl.LINEAR, mag: gl.LINEAR, mipmap: false }
  }

  /** 惰性生成 mipmap 链(每纹理一次); 供静态噪点与 pass 输出(mipmapNeeded)共用 */
  private ensureMipmap(gl: WebGL2RenderingContext, tex: WebGLTexture, tgt: number): void {
    if (this.mipmaped.has(tex)) return
    this.mipmaped.add(tex)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(tgt, tex)
    gl.generateMipmap(tgt)
  }

  /** 渲染一个 pass 到其离屏目标 / 屏幕 */
  private renderPass(rt: PassRuntime, elapsed: number, dt: number, mouse4: Float32Array, date4: Float32Array): void {
    const gl = this.gl!
    const c = this.canvas!
    const spec = rt.spec
    const w = rt.w || spec.w || c.width
    const h = rt.h || spec.h || c.height

    gl.useProgram(rt.prog)

    // 绑定 iChannel 纹理 + 应用显式采样参数(filter/wrap)
    for (let i = 0; i < 4; i++) {
      const ch = this.resolveChannel(rt, i)
      const tgt = ch.cube ? gl.TEXTURE_CUBE_MAP : gl.TEXTURE_2D
      gl.activeTexture(gl.TEXTURE0 + i)
      gl.bindTexture(tgt, ch.tex)
      if (ch.filter) {
        gl.texParameteri(tgt, gl.TEXTURE_MIN_FILTER, ch.filter.min)
        gl.texParameteri(tgt, gl.TEXTURE_MAG_FILTER, ch.filter.mag)
        if (ch.filter.mipmap) this.ensureMipmap(gl, ch.tex, tgt) // 噪点等静态纹理惰性生成
      }
      if (ch.wrap !== undefined) {
        gl.texParameteri(tgt, gl.TEXTURE_WRAP_S, ch.wrap)
        gl.texParameteri(tgt, gl.TEXTURE_WRAP_T, ch.wrap)
        if (ch.cube) gl.texParameteri(tgt, gl.TEXTURE_WRAP_R, ch.wrap)
      }
    }

    // 每帧 uniform
    const L = rt.uLoc
    if (L.iResolution) gl.uniform3f(L.iResolution, w, h, 1)
    if (L.iTime) gl.uniform1f(L.iTime, elapsed)
    if (L.iTimeDelta) gl.uniform1f(L.iTimeDelta, dt)
    if (L.iFrameRate) gl.uniform1f(L.iFrameRate, 1 / dt)
    if (L.iFrame) gl.uniform1i(L.iFrame, rt.frame++) // 每 pass 独立递增
    if (L.iMouse) gl.uniform4f(L.iMouse, mouse4[0], mouse4[1], mouse4[2], mouse4[3])
    if (L.iDate) gl.uniform4f(L.iDate, date4[0], date4[1], date4[2], date4[3])
    this.chanTime.fill(0) // 无视频/音频输入时 iChannelTime 全为 0
    if (rt.cTimeLoc) gl.uniform1fv(rt.cTimeLoc, this.chanTime)
    if (rt.resLoc) gl.uniform3fv(rt.resLoc, rt.chanRes)

    gl.bindFramebuffer(gl.FRAMEBUFFER, rt.fbo)
    if (spec.kind === CUBEMAP) {
      for (let k = 0; k < 6; k++) {
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, CUBE_FACES[k], rt.textures[0], 0)
        gl.viewport(0, 0, w, h)
        if (rt.faceLoc) gl.uniform1i(rt.faceLoc, k)
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
      }
    } else {
      if (rt.fbo && rt.textures.length > 1) {
        // 自反馈双纹理: 写 1-readIdx(本帧), 采读 readIdx(上一帧); 渲染后立即翻转
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, rt.textures[1 - rt.readIdx], 0)
      }
      gl.viewport(0, 0, w, h)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    // 渲染后立即翻转: 后续 pass 采样本 pass 时读到当前帧
    if (spec.kind !== IMAGE && spec.kind !== CUBEMAP && rt.textures.length > 1) rt.readIdx ^= 1
    // 输出被下游 mipmap 采样 → 对刚写入的纹理生成 mipmap 链
    if (rt.mipmapNeeded) {
      const out = rt.textures.length > 1 ? rt.textures[rt.readIdx] : rt.textures[0]
      this.ensureMipmap(gl, out, spec.kind === CUBEMAP ? gl.TEXTURE_CUBE_MAP : gl.TEXTURE_2D)
    }
  }

  private tick = () => {
    this.animId = requestAnimationFrame(this.tick) // 先调度下一帧, 保证 rAF 循环不中断
    const gl = this.gl!, c = this.canvas!
    if (!gl || !c) return
    const now = performance.now()
    // 帧率上限: 未到间隔直接跳过本帧渲染(定时器照常走)
    if (now - this.lastRenderTime < 1000 / FPS_CAP) return
    this.lastRenderTime = now
    const elapsed = (now - this.startTime) / 1000
    let dt = (now - this.prevTime) / 1000
    this.prevTime = now
    // 首帧/跳帧后/从后台恢复时 dt 异常, 钳制到正常帧间隔
    if (dt <= 0 || dt > 0.25) dt = 1 / 60

    const m = this.mouse
    const dpr = window.devicePixelRatio || 1
    this.mouse4[0] = m.x * dpr
    this.mouse4[1] = m.y * dpr
    this.mouse4[2] = m.down ? m.cx * dpr : -Math.abs(m.cx * dpr)
    // w 是"新按下信号"(按下后首帧为正, 之后为负, 近似点击位置语义)
    this.mouse4[3] = m.signal ? m.cy * dpr : -Math.abs(m.cy * dpr)
    m.signal = false

    const sec = Math.floor(now / 1000)
    if (sec !== this.lastDateSec) {
      this.lastDateSec = sec
      const d = new Date()
      this.cachedDate[0] = d.getFullYear()
      this.cachedDate[1] = d.getMonth() // 0-11, 与 iDate.y 语义一致(非 1-12)
      this.cachedDate[2] = d.getDate()
      this.cachedDate[3] = d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds() + d.getMilliseconds() / 1000
    }

    for (const p of this.passes) this.renderPass(p, elapsed, dt, this.mouse4, this.cachedDate)
  }

  resize(w: number, h: number): void {
    if (!this.canvas || !this.gl) return
    const dpr = window.devicePixelRatio || 1
    const pw = Math.floor(w * dpr)
    const ph = Math.floor(h * dpr)
    // 尺寸未变直接早退: resize 事件常连续触发相同尺寸, 避免无谓重建离屏目标
    if (this.canvas.width === pw && this.canvas.height === ph) return
    this.canvas.width = pw
    this.canvas.height = ph
    // 重建跟随画布尺寸的离屏目标(固定 iResolution / cubemap 固定 1024 的 pass 不受影响)
    for (const p of this.passes) {
      if (p.spec.kind === IMAGE || p.spec.kind === CUBEMAP || p.spec.w > 0 || p.spec.h > 0) continue
      for (const t of p.textures) this.gl.deleteTexture(t)
      if (p.fbo) this.gl.deleteFramebuffer(p.fbo)
      p.textures = []
      p.fbo = null
      this.allocTarget(p)
      this.refreshChannelRes(p, this.passes)
    }
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height)
  }

  private onContextLost = (e: Event) => {
    e.preventDefault() // 允许之后触发 webglcontextrestored
    this.setPasses([])
    this.programKey = ''
    this.vbuf = null
    this.mipmaped.clear() // 纹理随 context 销毁, 重建后需重新生成 mipmap
  }

  private onContextRestored = () => {
    const gl = this.gl, c = this.canvas
    if (!gl || !c) return
    try { this.setup(gl, c) } catch { /* 恢复失败则保持黑屏 */ }
  }

  private disposePass(rt: PassRuntime): void {
    const gl = this.gl
    if (!gl) return
    gl.deleteProgram(rt.prog)
    for (const t of rt.textures) gl.deleteTexture(t)
    for (const t of rt.noiseTex) if (t) gl.deleteTexture(t)
    if (rt.fbo) gl.deleteFramebuffer(rt.fbo)
  }

  private disposeRuntime(): void {
    for (const p of this.passes) this.disposePass(p)
    this.passes = []
  }

  destroy(): void {
    cancelAnimationFrame(this.animId)
    if (this.gl) {
      this.disposeRuntime()
      if (this.vbuf) this.gl.deleteBuffer(this.vbuf)
      this.vbuf = null
      this.gl.useProgram(null)
    }
    if (this.canvas) {
      this.canvas.removeEventListener('mousemove', this.onMouse)
      this.canvas.removeEventListener('mousedown', this.onMouse)
      this.canvas.removeEventListener('mouseup', this.onMouse)
      this.canvas.removeEventListener('mouseleave', this.onMouse)
      this.canvas.removeEventListener('webglcontextlost', this.onContextLost)
      this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored)
      this.canvas.remove(); this.canvas = null
    }
    this.gl = null
    this.programKey = ''
    this.mipmaped.clear()
  }
}

/** 某 channel 绑定结果: 纹理 + 显式采样参数(未指定为 undefined 则用纹理默认) */
interface ResolvedChannel {
  tex: WebGLTexture
  cube: boolean
  filter?: { min: number; mag: number; mipmap: boolean }
  wrap?: number
}